import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { LinearProgress } from "@mui/material";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../context/AuthContext";
import { useLoading } from "../../../context/LoadingContext";
import { DateTime } from "luxon";
import { getEventList, getEventListCheckIn } from "../../../axios/Eventlog/EventLogApi";
import NoRowOverlay from "../../../assets/images/icons/NoRowOverlay";

dayjs.extend(utc);
dayjs.extend(timezone);

const RecentCheckInList = ({
    searchText,
    selectedDate,
    groupId,
    refreshData,
    dispalyWeek
}) => {
    const { t, i18n } = useTranslation();

    const {
        genaralSettingTimeZone,
        dateTimeFormat
    } = useAuth();

    const [show, hide] = useLoading();

    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);

    const buildFromToByDateWithTZ = (selectedDate, timeZone) => {
        const base = DateTime.fromISO(
            typeof selectedDate === "string"
                ? selectedDate
                : DateTime.fromJSDate(selectedDate).toISODate(),
            { zone: timeZone }
        );

        return {
            from: base.startOf("day").toUTC().toISO(),
            to: base.endOf("day").toUTC().toISO(),
        };
    };

    useEffect(() => {
        const fetchData = async () => {
            const { from, to } = buildFromToByDateWithTZ(
                selectedDate,
                genaralSettingTimeZone || "UTC"
            );

            try {
                show();

                const { data: response } = await getEventListCheckIn(
                    1,
                    1000,
                    from,
                    to,
                    "",
                    "",
                    false,
                    true,
                    localStorage.getItem("i18nextLngId"),
                    groupId
                );

                const events =
                    Array.isArray(response?.data)
                        ? response.data
                        : [];

                setData(events);
            } catch (error) {
                if (error?.response?.status !== 401) {
                    toast.error(
                        error?.response?.data
                            ? t(error.response.data)
                            : t("common.network-timeout")
                    );
                }
            } finally {
                hide();
            }
        };

        fetchData();
    }, [
        selectedDate,
        groupId,
        refreshData,
        genaralSettingTimeZone,
        i18n.language,
        t,
        show,
        hide,
    ]);

    const convertDateFnsToDayjs = (format) => {
        return format
            .replace(/yyyy/g, "YYYY")
            .replace(/dd/g, "DD");
    };

    const formatTime = (value) => {
        if (!value) return "-";

        return dayjs
            .utc(value)
            .tz(dayjs.tz.guess())
            .format(convertDateFnsToDayjs(dateTimeFormat));
    };

    const getStatusInfo = (status) => {
        const upper = String(status || "").toUpperCase();

        if (upper === "PENDING_CHECK_IN") {
            return {
                text: t(`attendance.${upper}`),
                color: "#F97C00",
            };
        }

        if (upper === "CHECK_IN") {
            return {
                text: t(`attendance.${upper}`),
                color: "#058900",
            };
        }

        return {
            text: t(`attendance.${upper}`),
            color: "#6B7280",
        };
    };

    const filteredData = React.useMemo(() => {
        if (!searchText?.trim()) return data;

        const keyword = searchText.toLowerCase().trim();

        return data.filter((item) =>
            [
                item.fullName,
                item.employeeCode,
                item.eventType,
                item.shiftName,
            ]
                .filter(Boolean)
                .some((value) =>
                    String(value)
                        .toLowerCase()
                        .includes(keyword)
                )
        );
    }, [data, searchText]);

    return (
        <div
            className="flex flex-col"
            style={{
                border: "1px solid #d9d9d9",
                borderRadius: 16,
                overflow: "hidden",
                background: "#fff",
                height: dispalyWeek === 5 ? 569 : 635,
            }}
        >
            {loading && <LinearProgress />}

            {!loading && filteredData.length === 0 && (
                <div className="flex min-h-0 flex-1 items-center justify-center">
                    <NoRowOverlay />
                </div>
            )}

            {!loading && filteredData.length > 0 && (
            <div
                className="min-h-0 flex-1"
                style={{ overflowX: "hidden", overflowY: "auto" }}
            >
                {filteredData.map((item, index) => {
                    const status = getStatusInfo(item.eventType);

                    return (
                        <div
                            key={item.id || index}
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                padding: "12px 14px",
                                borderBottom:
                                    index !== data.length - 1
                                        ? "1px solid #e5e7eb"
                                        : "none",
                            }}
                        >
                            <div>
                                <div
                                    style={{
                                        fontSize: 14,
                                        fontWeight: 500,
                                        color: "#1F2937",
                                    }}
                                >
                                    {item.fullName || "-"}
                                </div>

                                <div
                                    style={{
                                        marginTop: 4,
                                        fontSize: 12,
                                        color: "#6B7280",
                                    }}
                                >
                                    {formatTime(
                                        item.timeGMT
                                    )}
                                </div>
                            </div>

                            <div
                                style={{
                                    color: status.color,
                                    fontWeight: 500,
                                    fontSize: 14,
                                }}
                            >
                                {status.text}
                            </div>
                        </div>
                    );
                })}
            </div>
            )}
        </div>
    );
};

export default RecentCheckInList;